// Thin fetch wrappers for the public booking endpoints exposed by the admin
// backend (admin.seadent.es). Cross-origin from seadent.es; CORS is configured
// server-side. No auth — these endpoints are intentionally unauthenticated.

import { PUBLIC_API_BASE_URL } from '../config/apiBase';

const API_BASE_URL = PUBLIC_API_BASE_URL;

export interface AvailabilityDay {
  date: string;            // 'YYYY-MM-DD'
  hasSlots: boolean;
  count: number;
}

export interface AvailabilitySlot {
  start_time: string;      // 'HH:mm'
  end_time: string;
}

export interface HoldResponse {
  holdToken: string;
  expiresAt: string;       // ISO
  end_time: string;
}

/** Urlaub / Betriebsferien — Online-Buchung für einen Zeitraum gesperrt. */
export interface ClosureStatus {
  /** Die Sperre greift heute. */
  active: boolean;
  /** Sie ist eingetragen, beginnt aber erst später — ihre Tage fehlen trotzdem schon. */
  upcoming: boolean;
  /** 'YYYY-MM-DD', erster gesperrter Tag. */
  from: string;
  /** 'YYYY-MM-DD', letzter gesperrter Tag (einschließlich). */
  until: string;
}

/**
 * Die Regeln, an die sich die Buchungsstrecke halten muss — vom Server, nicht
 * im Text festgeschrieben. Ändert die Praxis die Karenzzeit, ändert sich der
 * Hinweis auf der Website mit.
 */
export interface BookingRules {
  leadWorkingDays: number;
  earliestDate: string;        // 'YYYY-MM-DD'
  latestEndTime: string | null;
  closure: ClosureStatus;
}

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') u.set(k, String(v));
  });
  return u.toString();
};

const ProcedureRefSchema = (procedureId?: number, procedureKey?: string) => {
  if (!procedureId && !procedureKey) {
    throw new Error('Either procedureId or procedureKey is required');
  }
  return { procedureId, procedureKey };
};

export async function getAvailabilityRange(opts: {
  procedureId?: number;
  procedureKey?: string;
  from: string;
  to: string;
}): Promise<AvailabilityDay[]> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const qs = buildQuery({ ...ref, from: opts.from, to: opts.to });
  const r = await fetch(`${API_BASE_URL}/public/availability?${qs}`);
  if (!r.ok) throw new Error(`availability range failed: ${r.status}`);
  const j = await r.json();
  return Array.isArray(j.days) ? j.days : [];
}

export async function getAvailabilityDay(opts: {
  procedureId?: number;
  procedureKey?: string;
  date: string;
}): Promise<AvailabilitySlot[]> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const qs = buildQuery({ ...ref, date: opts.date });
  const r = await fetch(`${API_BASE_URL}/public/availability/day?${qs}`);
  if (!r.ok) throw new Error(`availability day failed: ${r.status}`);
  const j = await r.json();
  return Array.isArray(j.slots) ? j.slots : [];
}

export async function createHold(opts: {
  procedureId?: number;
  procedureKey?: string;
  date: string;
  time: string;
}): Promise<HoldResponse> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const r = await fetch(`${API_BASE_URL}/public/holds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({ ...ref, date: opts.date, time: opts.time }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(j?.message || `hold failed: ${r.status}`);
    err.status = r.status;
    err.code = j?.reason || j?.code;
    throw err;
  }
  return { holdToken: j.holdToken, expiresAt: j.expiresAt, end_time: j.end_time };
}

export async function releaseHold(token: string): Promise<void> {
  // Best-effort: ignore failures, the hold expires on its own.
  try {
    await fetch(`${API_BASE_URL}/public/holds/${encodeURIComponent(token)}`, { method: 'DELETE' });
  } catch { /* ignore */ }
}

/**
 * Buchungsregeln lesen. Ohne Behandlung kommen die Praxis-Defaults — das reicht
 * für den Hinweisbanner, der auf jeder Seite steht.
 *
 * Fail-soft: bei jeder Störung `null`. Die Buchungsstrecke zeigt dann keinen
 * Regel-Hinweis, bleibt aber bedienbar; der Server prüft ohnehin erneut.
 */
export async function getBookingRules(opts: {
  procedureId?: number;
  procedureKey?: string;
} = {}): Promise<BookingRules | null> {
  try {
    const qs = buildQuery({ procedureId: opts.procedureId, procedureKey: opts.procedureKey });
    const r = await fetch(`${API_BASE_URL}/public/booking-rules${qs ? `?${qs}` : ''}`);
    if (!r.ok) return null;
    const j = await r.json();
    if (!j?.success) return null;
    return {
      leadWorkingDays: Number.isFinite(j.leadWorkingDays) ? j.leadWorkingDays : 0,
      earliestDate: typeof j.earliestDate === 'string' ? j.earliestDate : '',
      latestEndTime: typeof j.latestEndTime === 'string' ? j.latestEndTime : null,
      closure: {
        active: Boolean(j.closure?.active),
        upcoming: Boolean(j.closure?.upcoming),
        from: typeof j.closure?.from === 'string' ? j.closure.from : '',
        until: typeof j.closure?.until === 'string' ? j.closure.until : '',
      },
    };
  } catch {
    return null;
  }
}
